import type { CompanyProfile } from '@jfh/intake-shared';
import {
  companyNameCase,
  companyNotActiveMessage,
  companyStatusLabel,
  isActiveCompany,
  normaliseCompanyNumber,
} from '@jfh/intake-shared';
import type { CompanyLookupOutcome } from './api.js';

/** Where a company-number field's Companies House lookup has got to (F8). */
export type CompanyState =
  | { kind: 'checking'; number: string }
  | { kind: 'found'; number: string; company: CompanyProfile }
  | { kind: 'not_found'; number: string }
  | { kind: 'busy'; number: string }
  | { kind: 'unavailable'; number: string };

export type StatusTone = 'info' | 'ok' | 'warn' | 'error';

/**
 * The number to look up, once the applicant has typed a whole one (8
 * characters ignoring spaces); null while it is still partial or isn't a
 * company number.
 */
export function completeCompanyNumber(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  return raw.replace(/\s+/g, '').length >= 8 ? normaliseCompanyNumber(raw) : null;
}

export function stateFromLookup(number: string, r: CompanyLookupOutcome): CompanyState {
  if (r.kind !== 'found') return { kind: r.kind, number };
  // A company that isn't active is still used, with a warning (see below).
  return { kind: 'found', number, company: r.company };
}

/** The line shown under the company-number input. */
export function companyStatusLine(s: CompanyState | undefined): { tone: StatusTone; text: string } | null {
  switch (s?.kind) {
    case undefined:
      return null;
    case 'checking':
      return { tone: 'info', text: 'Checking Companies House…' };
    case 'found':
      if (!isActiveCompany(s.company.status)) return { tone: 'warn', text: companyNotActiveMessage(s.company) };
      return {
        tone: 'ok',
        text: `Found ${companyNameCase(s.company.name)} (${companyStatusLabel(s.company.status).toLowerCase()}). We've filled in your company name and registered address from Companies House.`,
      };
    case 'not_found':
      return { tone: 'error', text: "We couldn't find that company number at Companies House. Check it and try again." };
    case 'busy':
      return { tone: 'warn', text: 'Too many lookups. Please wait a minute, then change the number to try again.' };
    case 'unavailable':
      return {
        tone: 'warn',
        text: "We couldn't reach Companies House just now, so please type your company details. We'll check them before opening your account.",
      };
  }
}

/**
 * The error that stops the applicant continuing past an answered company
 * number, or null. `raw` is the typed value; required-when-empty is handled
 * by the normal field validation.
 */
export function companyFieldError(raw: unknown, s: CompanyState | undefined): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  if (!completeCompanyNumber(raw)) return 'Enter all 8 characters of the company number, for example 01234567 or SC123456.';
  switch (s?.kind) {
    case undefined:
    case 'checking':
      return 'Checking Companies House. Please wait a moment.';
    case 'not_found':
    case 'busy':
      return companyStatusLine(s)!.text;
    default:
      return null; // found, or Companies House unavailable (checked again on submit)
  }
}
